import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Phone, MessageCircle, ShieldCheck,
  Leaf, Award, Clock, Wallet, Star, ChevronDown, Sparkles,
  UtensilsCrossed, Hotel, Building2, Stethoscope, GraduationCap,
  ShoppingBag, ShoppingCart, ChefHat, Landmark, Pill, Scissors,
  Dumbbell, Film, Warehouse, Utensils, Truck, BedDouble, Coffee,
  PawPrint, Theater, Factory, Snowflake, FlaskConical, Shirt,
  TestTube, Car, Hammer, Construction, Zap, Fuel, FileText,
  Boxes, Beer, Cpu, Milk, Wheat, Ship, HelpCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { getServiceBySlug, CATEGORIES } from '../data/servicesData';
import { SiteHeader, SiteFooter } from './ServicesIndex';

const FIELD_ICON_MAP = {
  UtensilsCrossed, Hotel, Building2, Stethoscope, GraduationCap,
  ShoppingBag, ShoppingCart, ChefHat, Landmark, Pill, Scissors,
  Dumbbell, Film, Warehouse, Utensils, Truck, BedDouble, Coffee,
  PawPrint, Theater, Factory, Snowflake, FlaskConical, Shirt,
  TestTube, Car, Hammer, Construction, Zap, Fuel, FileText,
  Boxes, Beer, Cpu, Milk, Wheat, Ship, HelpCircle,
};

const WHY_CHOOSE = [
  { Icon: Award, title: 'Certified Professionals', desc: 'Govt-licensed technicians with 15+ years of experience.' },
  { Icon: Leaf, title: 'Eco-Friendly Chemicals', desc: 'ISI / WHO-approved, odourless and biodegradable.' },
  { Icon: Clock, title: 'Fast Response Time', desc: 'Same-day appointment slots across Uttarakhand.' },
  { Icon: Wallet, title: 'Affordable Pricing', desc: 'Transparent quotes — no hidden charges.' },
  { Icon: ShieldCheck, title: 'Guaranteed Results', desc: 'Free re-treatment if pests return within warranty.' },
  { Icon: Star, title: '10,000+ Happy Homes', desc: 'Trusted across Dehradun, Haridwar, Rishikesh & more.' },
];

const SAFETY_LIST = [
  { title: 'Family-Safe', desc: 'Completely safe for children and the elderly after 2-hour drying.' },
  { title: 'Pet-Safe', desc: 'Cats, dogs and other pets can return to the space the same day.' },
  { title: 'Govt-Approved', desc: 'Only CIB-registered, WHO-tested pesticides.' },
  { title: 'Odourless', desc: 'No lingering smell — no need to vacate during treatment.' },
];

export default function ServiceDetail({ onOpenCalculator }) {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useSeo(
    service?.name,
    service ? `${service.shortDesc} Book ${service.name} with EntoRid — ${service.seoKeywords}.` : undefined,
  );

  if (!service) return <Navigate to="/services" replace />;

  const category = CATEGORIES.find(c => c.id === service.category);

  // If on commercial/industrial page, pre-lock the calculator segment
  const segmentLock = (slug === 'commercial' || slug === 'industrial') ? slug : null;
  const openCalc = () => onOpenCalculator(null, segmentLock);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-4 text-sm text-gray-500 overflow-x-auto whitespace-nowrap" data-testid="breadcrumb">
          <Link to="/" className="hover:text-brand-green">Home</Link>
          <span className="mx-2">›</span>
          <Link to="/services" className="hover:text-brand-green">Services</Link>
          <span className="mx-2">›</span>
          <Link to={`/services#${category.id}`} className="hover:text-brand-green">{category.label}</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">{service.name}</span>
        </div>
      </div>

      {/* A. HERO */}
      <section
        data-testid="detail-hero"
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(10,37,64,0.92) 0%, rgba(30,58,95,0.82) 60%, rgba(0,168,107,0.75) 100%), url(${service.heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className={`inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 px-3.5 py-1.5 text-xs sm:text-sm font-semibold text-white`}>
            {category.label}
          </span>
          <h1 className="mt-4 text-3xl sm:text-5xl font-bold tracking-tight max-w-3xl" data-testid="detail-title">
            {service.name}
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-white/85 max-w-2xl">{service.tagline}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              onClick={openCalc}
              data-testid="detail-book-btn"
              className="h-12 px-7 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-lg"
            >
              Book Service Now <ArrowRight className="ml-2" size={18} />
            </Button>
            <a
              href="https://api.whatsapp.com/send?phone=919410110910"
              target="_blank"
              rel="noreferrer"
              data-testid="detail-wa-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/10 text-white border border-white/30 hover:bg-white/20 font-semibold transition-colors"
            >
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <main className="pb-24 lg:pb-12">
        {/* B. ABOUT THE PEST */}
        <Section id="about" title={`About ${service.name}`}>
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-brand-blue mb-2">What is it?</h3>
              <p className="text-gray-700 leading-relaxed">{service.about.what}</p>
              <h3 className="text-lg font-semibold text-brand-blue mt-6 mb-2">Why it's dangerous</h3>
              <p className="text-gray-700 leading-relaxed">{service.about.danger}</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 ring-1 ring-gray-100">
              <h3 className="text-lg font-semibold text-brand-blue mb-4">Where it's commonly found</h3>
              <ul className="grid grid-cols-2 gap-2">
                {service.about.common.map((loc) => (
                  <li key={loc} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 size={16} className="text-brand-green mt-0.5 flex-shrink-0" />
                    <span>{loc}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        {/* B-2. INDUSTRIES WE SERVE (commercial/industrial icon grid) */}
        {service.industryFields?.length > 0 && (
          <Section
            id="industries"
            title="Industries We Serve"
            subtitle="Audit-ready pest control tailored to every site"
            bg="slate"
          >
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4"
              data-testid="commercial-fields-grid"
            >
              {service.industryFields.map((field) => {
                const Icon = FIELD_ICON_MAP[field.icon] || Building2;
                return (
                  <div
                    key={field.label}
                    data-testid={`commercial-field-${field.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className="group relative bg-white rounded-2xl p-4 sm:p-5 ring-1 ring-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:ring-emerald-300 transition-all duration-300 cursor-pointer overflow-hidden"
                  >
                    <div
                      aria-hidden
                      className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 via-emerald-500/0 to-emerald-500/0 group-hover:from-emerald-500/5 group-hover:via-emerald-500/0 group-hover:to-emerald-500/10 transition-all duration-300"
                    />
                    <div className="relative">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3 group-hover:bg-emerald-600 group-hover:text-white group-hover:scale-110 transition-all duration-300">
                        <Icon size={24} strokeWidth={2} />
                      </div>
                      <h3 className="font-semibold text-brand-blue text-sm sm:text-[15px] leading-tight">{field.label}</h3>
                      <p className="mt-1.5 text-xs sm:text-[13px] text-gray-500 leading-relaxed line-clamp-2 group-hover:text-gray-700 transition-colors">
                        {field.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto" data-testid="industries-note">
              Don&apos;t see your industry?{' '}
              <a
                href="https://api.whatsapp.com/send?phone=919410110910"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-700 font-semibold hover:underline"
              >
                Talk to our experts on WhatsApp
              </a>
              {' '}— we customise programs for any premises.
            </p>
          </Section>
        )}

        {/* C. SIGNS */}
        <Section id="signs" title="Signs of Infestation" subtitle="How to know it's time to call us" dark>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.signs.map((sign, idx) => (
              <li key={idx} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10" data-testid={`sign-${idx}`}>
                <div className="w-8 h-8 rounded-full bg-brand-green/20 text-brand-green-light flex items-center justify-center flex-shrink-0 text-xs font-bold">{idx + 1}</div>
                <span className="text-white/90 text-[15px]">{sign}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* D. PROCESS */}
        <Section id="process" title="Our Treatment Process" subtitle="4 simple steps · transparent · documented">
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.process.map((step, idx) => (
              <li key={idx} className="relative bg-white rounded-2xl p-6 ring-1 ring-gray-100 shadow-sm" data-testid={`process-step-${idx + 1}`}>
                <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-brand-green text-white font-bold flex items-center justify-center shadow-md">
                  {idx + 1}
                </div>
                <h3 className="mt-5 font-semibold text-brand-blue mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </li>
            ))}
          </ol>
        </Section>

        {/* E. WHY CHOOSE */}
        <Section id="why" title="Why Choose EntoRid" subtitle="The difference that matters" bg="slate">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map(({ Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-5 ring-1 ring-gray-100">
                <div className="w-11 h-11 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center mb-3">
                  <Icon size={20} />
                </div>
                <h3 className="font-semibold text-brand-blue mb-1">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* F. SAFETY */}
        <Section id="safety" title="Safety First" subtitle="Our commitment to your family's well-being">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SAFETY_LIST.map((s) => (
              <div key={s.title} className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-5 ring-1 ring-emerald-100" data-testid={`safety-${s.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <ShieldCheck size={24} className="text-brand-green mb-3" />
                <h3 className="font-semibold text-brand-blue mb-1">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* H. FAQ */}
        <Section id="faq" title="Frequently Asked Questions" subtitle="Everything you need to know before booking">
          <div className="max-w-3xl mx-auto space-y-3">
            {service.faqs.map((f, idx) => <FaqItem key={idx} idx={idx} faq={f} />)}
          </div>
        </Section>

        {/* I. FINAL CTA */}
        <section className="relative overflow-hidden py-14 sm:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-green" />
          <div className="relative max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center text-white">
            <Sparkles size={32} className="mx-auto mb-4 text-brand-green-light" />
            <h2 className="text-2xl sm:text-4xl font-bold leading-tight" data-testid="detail-final-cta-title">
              Book Your {service.name.replace(' Treatment', '')} Service Today
            </h2>
            <p className="mt-3 text-base sm:text-lg text-white/85">
              Same-day appointments · Certified experts · Guaranteed results
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button
                onClick={openCalc}
                data-testid="detail-final-book-btn"
                className="h-12 px-7 rounded-full bg-white text-brand-blue hover:bg-white/95 font-semibold shadow-lg"
              >
                Book Service Now
              </Button>
              <a
                href="tel:+917819988010"
                data-testid="detail-final-call-btn"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-brand-green hover:bg-brand-green-dark text-white font-semibold shadow-lg"
              >
                <Phone size={18} /> Call +91-7819988010
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function Section({ id, title, subtitle, children, dark, bg }) {
  const bgClass = dark
    ? 'bg-brand-blue text-white'
    : bg === 'slate' ? 'bg-slate-50' : 'bg-white';
  const titleClass = dark ? 'text-white' : 'text-brand-blue';
  return (
    <section id={id} className={`py-14 sm:py-20 ${bgClass}`} data-testid={`section-${id}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-12">
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold ${titleClass}`}>{title}</h2>
          {subtitle && <p className={`mt-2 text-base ${dark ? 'text-white/70' : 'text-gray-600'}`}>{subtitle}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

function FaqItem({ idx, faq }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden" data-testid={`faq-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span className="font-semibold text-brand-blue text-[15px]">{faq.q}</span>
        <ChevronDown size={18} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 text-sm text-gray-700 leading-relaxed border-t border-gray-100">
          {faq.a}
        </div>
      )}
    </div>
  );
}
